import { z } from 'zod';
import { paginationQuerySchema } from '../common/envelope.js';
/** Independent of `stage.type` (open | won | lost, no "abandoned") on
 * purpose — see the field's own comment in schema.prisma. Left out of
 * `createDealSchema` for the normal New Opportunity form (a brand-new
 * opportunity is always 'open'; DealsService#create defaults it there),
 * but accepted on create by CSV import for backfilling historical deals
 * with a known outcome. */
export const dealStatusSchema = z.enum(['open', 'won', 'lost', 'abandoned']);
export const createDealSchema = z.object({
    name: z.string().trim().min(1).max(200),
    pipelineId: z.string().uuid(),
    stageId: z.string().uuid(),
    amount: z.coerce.number().nonnegative(),
    currency: z.string().length(3).default('USD'),
    expectedCloseDate: z.string().date().optional(),
    companyId: z.string().uuid().optional(),
    primaryContactId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    source: z.string().trim().max(200).optional(),
    sourceLeadId: z.string().uuid().optional(),
    status: dealStatusSchema.optional(),
    customFields: z.record(z.string(), z.unknown()).default({}),
});
export const updateDealSchema = createDealSchema.partial().extend({
    version: z.number().int().nonnegative(),
});
/** Moving stage is its own endpoint (not a generic PATCH) because it has its
 * own side effects — stage history, won/lost timestamps, a loss reason
 * requirement (docs/architecture screen "Deals — pipeline board"). */
export const moveDealStageSchema = z.object({
    stageId: z.string().uuid(),
    lossReasonId: z.string().uuid().optional(),
});
export const listDealsQuerySchema = paginationQuerySchema.extend({
    pipelineId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    status: dealStatusSchema.optional(),
    expectedCloseDateFrom: z.string().date().optional(),
    expectedCloseDateTo: z.string().date().optional(),
    amountMin: z.coerce.number().nonnegative().optional(),
    amountMax: z.coerce.number().nonnegative().optional(),
});
export const dealResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    pipeline: z.object({ id: z.string().uuid(), name: z.string() }),
    stage: z.object({ id: z.string().uuid(), name: z.string(), probability: z.number(), type: z.string() }),
    amount: z.string(),
    currency: z.string(),
    expectedCloseDate: z.string().nullable(),
    company: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    owner: z.object({ id: z.string().uuid(), displayName: z.string() }).nullable(),
    primaryContact: z.object({ id: z.string().uuid(), displayName: z.string() }).nullable(),
    source: z.string().nullable(),
    status: dealStatusSchema,
    lossReason: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    stageEnteredAt: z.string().datetime(),
    wonAt: z.string().datetime().nullable(),
    lostAt: z.string().datetime().nullable(),
    /** Only populated by the single-deal GET (docs screen "Deal — detail");
     * `null` on every list() row rather than paying for a lead+audit-log
     * lookup per row on the board. */
    createdFromLead: z
        .object({
        id: z.string().uuid(),
        name: z.string(),
        sourceName: z.string().nullable(),
        score: z.number().int().nullable(),
        ageDays: z.number().int(),
        convertedByName: z.string().nullable(),
    })
        .nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().int(),
});
/** docs screen "Deal — detail" § Stage history — read from
 * OpportunityStageHistory, which DealsService#moveStage already writes on
 * every transition. */
export const dealStageHistoryEntrySchema = z.object({
    stageId: z.string().uuid(),
    stageName: z.string(),
    enteredAt: z.string().datetime(),
    durationSeconds: z.number().int().nullable(),
    isCurrent: z.boolean(),
});
export const dealContactSchema = z.object({
    contactId: z.string().uuid(),
    displayName: z.string(),
    jobTitle: z.string().nullable(),
    role: z.string().nullable(),
    isPrimary: z.boolean(),
});
export const dealLineItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    quantity: z.string(),
    unitPrice: z.string(),
    discount: z.string(),
    total: z.string(),
});
export const createDealLineItemSchema = z.object({
    name: z.string().trim().min(1).max(200),
    quantity: z.coerce.number().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    discount: z.coerce.number().nonnegative().default(0),
});
export const attachDealContactSchema = z.object({
    contactId: z.string().uuid(),
    role: z.string().trim().max(60).optional(),
    isPrimary: z.boolean().default(false),
});
/** Board header/column stats (docs screen "Deals — pipeline board"): real
 * aggregates over every open/won/lost deal in the pipeline, not capped by
 * the shared list endpoint's pageSize=100 max — a board with more deals
 * than one page can still show an accurate "N open · $X · weighted $Y". */
export const dealPipelineStageSummarySchema = z.object({
    stageId: z.string().uuid(),
    count: z.number().int(),
    totalValue: z.string(),
});
export const dealPipelineSummaryResponseSchema = z.object({
    pipelineId: z.string().uuid(),
    openCount: z.number().int(),
    openValue: z.string(),
    weightedValue: z.string(),
    pastDueCount: z.number().int(),
    stages: z.array(dealPipelineStageSummarySchema),
});
//# sourceMappingURL=schemas.js.map